// [COPIAR ARQUIVO]

/* GS://PROTOCOLO
 * Arquivo: C:\Users\Usuario\GoldenSisters_Project\goldensisters_admin_web\src\pages\assembly\AssemblyOrderDetailPage.js
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 1.1 – Fundo branco na Tela D (FASE 4).
 * Impacto da Versão: [src\pages\assembly\AssemblyOrderDetailPage.js]
 * Data: 28/11/2025
 * Responsável: ChatGPT (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - Ajustado container principal para fundo branco padrão.
 * - Atualizados cards e textos para paleta clara, mantendo identidade GoldenSisters.
 * - Mantida leitura pura da Ordem de Montagem (sem qualquer ajuste de estoque).
 * - Mantida navegação de volta para a TELA A (/assembly/orders).
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { GSM_COLLECTIONS } from '../../services/gs_nf_module_dataModel';
import {
  ArrowLeft,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
  Play,
  XCircle,
  Loader,
  Link as LinkIcon,
} from 'lucide-react';

const STATUS_LABELS = {
  ABERTA: 'Aberta',
  EM_EXECUCAO: 'Em execução',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

const STATUS_COLORS = {
  ABERTA: 'bg-yellow-50 text-yellow-800 border border-yellow-300',
  EM_EXECUCAO: 'bg-blue-50 text-blue-800 border border-blue-300',
  CONCLUIDA: 'bg-emerald-50 text-emerald-800 border border-emerald-300',
  CANCELADA: 'bg-red-50 text-red-800 border border-red-300',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    if (value.toDate) {
      return value.toDate().toLocaleString('pt-BR');
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR');
  } catch {
    return '-';
  }
};

export default function AssemblyOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadOrder = async () => {
      if (!orderId) {
        setError('Ordem de Montagem não informada.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const ref = doc(collection(db, GSM_COLLECTIONS.ASSEMBLIES), orderId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setError('Ordem de Montagem não encontrada.');
          setOrder(null);
        } else {
          const data = snap.data() || {};
          setOrder({
            id: snap.id,
            status: data.status || 'ABERTA',
            orderReference: data.orderReference || '',
            orderDescription: data.orderDescription || '',
            orderQuantity: data.orderQuantity || 1,
            lotId: data.lotId || null,
            nfId: data.nfId || null,
            finalSku: data.finalSku || '',
            assemblyType: data.assemblyType || 'SIMPLES',
            createdAt: data.createdAt || null,
            createdByUserId: data.createdByUserId || '',
            updatedAt: data.updatedAt || null,
            updatedByUserId: data.updatedByUserId || '',
          });
        }
      } catch (err) {
        console.error('Erro ao carregar detalhe da Ordem de Montagem:', err);
        setError('Não foi possível carregar o detalhe da Ordem de Montagem.');
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderId]);

  const handleBack = () => {
    navigate('/assembly/orders');
  };

  const renderStatusBadge = (statusRaw) => {
    const status = statusRaw || 'ABERTA';
    const label = STATUS_LABELS[status] || status;
    const badgeClass =
      STATUS_COLORS[status] ||
      'bg-gray-100 text-gray-700 border border-gray-300';

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badgeClass}`}
      >
        {status === 'CONCLUIDA' && (
          <CheckCircle2 className="w-3 h-3 mr-1.5" />
        )}
        {status === 'CANCELADA' && (
          <XCircle className="w-3 h-3 mr-1.5" />
        )}
        {status === 'EM_EXECUCAO' && (
          <Play className="w-3 h-3 mr-1.5" />
        )}
        {status === 'ABERTA' && (
          <AlertCircle className="w-3 h-3 mr-1.5" />
        )}
        {label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white text-brand-black px-6 py-6">
      {/* Cabeçalho + voltar */}
      <div className="flex flex-col gap-4 mb-6">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center text-sm text-gray-600 hover:text-brand-gold hover:underline w-fit"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar para Ordens de Montagem
        </button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="flex items-center text-2xl font-semibold text-brand-gold">
              <ClipboardList className="w-7 h-7 mr-3" />
              Detalhe da Ordem de Montagem
            </h1>
            <p className="mt-1 text-sm text-gray-600 max-w-2xl">
              Visualização da Ordem de Montagem. Nesta fase, este detalhe é apenas leitura,
              sem movimentação de estoque ou edições.
            </p>
          </div>

          {order && (
            <div className="flex flex-col items-end gap-2 text-sm">
              <div className="text-xs text-gray-500">
                ID interno:
                <span className="ml-1 text-gray-900 font-mono">
                  {order.id}
                </span>
              </div>
              {renderStatusBadge(order.status)}
            </div>
          )}
        </div>
      </div>

      {/* Estado de carregamento / erro */}
      {loading && (
        <div className="flex items-center justify-center py-10 text-gray-700">
          <Loader className="w-6 h-6 mr-2 animate-spin text-brand-gold" />
          Carregando Ordem de Montagem...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && order && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Bloco principal */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Dados principais
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="block text-xs text-gray-500 mb-1">
                    Referência / SKU
                  </span>
                  <p className="text-gray-900 font-semibold">
                    {order.orderReference || order.finalSku || '—'}
                  </p>
                  {order.finalSku && (
                    <p className="text-xs text-gray-600 mt-1">
                      SKU final:{' '}
                      <span className="text-gray-900 font-medium">
                        {order.finalSku}
                      </span>
                    </p>
                  )}
                </div>

                <div>
                  <span className="block text-xs text-gray-500 mb-1">
                    Tipo de Montagem
                  </span>
                  <p className="text-gray-900">
                    {order.assemblyType === 'COMPLEXA'
                      ? 'Montagem Complexa'
                      : 'Montagem Simples'}
                  </p>
                </div>

                <div>
                  <span className="block text-xs text-gray-500 mb-1">
                    Quantidade de conjuntos
                  </span>
                  <p className="text-gray-900 font-medium">
                    {order.orderQuantity || 1}
                  </p>
                </div>

                <div>
                  <span className="block text-xs text-gray-500 mb-1">
                    Status atual
                  </span>
                  {renderStatusBadge(order.status)}
                </div>
              </div>

              <div className="mt-4">
                <span className="block text-xs text-gray-500 mb-1">
                  Descrição / Observações da Montagem
                </span>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {order.orderDescription || 'Nenhuma descrição registrada.'}
                </p>
              </div>
            </div>

            {/* Placeholder para itens/componentes – futura fase */}
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Itens / Componentes da Montagem
              </h2>
              <p className="text-sm text-gray-600">
                Nesta fase do planejamento, os itens da montagem ainda não estão
                integrados. Este bloco será usado futuramente para listar as peças
                consumidas do estoque e o produto comercial resultante.
              </p>
            </div>
          </div>

          {/* Bloco lateral – vínculos e auditoria */}
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                Vínculos (Lote / NF)
              </h2>

              <div className="space-y-2 text-xs text-gray-700">
                <div>
                  <span className="text-gray-500">Lote vinculado:</span>
                  <p className="text-gray-900 font-medium">
                    {order.lotId || 'Nenhum lote vinculado.'}
                  </p>
                </div>
                <div className="mt-2">
                  <span className="text-gray-500">NF vinculada:</span>
                  <p className="text-gray-900 font-medium">
                    {order.nfId || 'Nenhuma NF vinculada.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Auditoria
              </h2>
              <div className="space-y-2 text-xs text-gray-700">
                <div>
                  <span className="text-gray-500">Criado em:</span>
                  <p className="text-gray-900 font-medium">
                    {formatDateTime(order.createdAt)}
                  </p>
                </div>
                {order.createdByUserId && (
                  <div>
                    <span className="text-gray-500">
                      Criado por (ID usuário):
                    </span>
                    <p className="text-gray-900 font-mono">
                      {order.createdByUserId}
                    </p>
                  </div>
                )}

                <div className="mt-3">
                  <span className="text-gray-500">Última atualização:</span>
                  <p className="text-gray-900 font-medium">
                    {order.updatedAt ? formatDateTime(order.updatedAt) : '—'}
                  </p>
                </div>
                {order.updatedByUserId && (
                  <div>
                    <span className="text-gray-500">
                      Atualizado por (ID usuário):
                    </span>
                    <p className="text-gray-900 font-mono">
                      {order.updatedByUserId}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
